import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useLanguage } from '../hooks/useLanguage';
import api, { authAPI } from '../services/api';
import { UserIcon, CameraIcon, Crown, Zap, InfoIcon } from '../components/Icons';
import UpgradeModal from '../components/UpgradeModal';
import { TIER_FEATURES } from '../constants/subscription';

interface QuestionByContract {
  document_id: number;
  document_name: string;
  document_type: string;
  created_at: string | null;
  total_questions: number;
  questions_this_month: number;
  recent_questions: Array<{
    id: number;
    question: string;
    answer: string;
    created_at: string | null;
  }>;
}

interface QuestionsByContractResponse {
  total_questions_all_time: number;
  total_questions_this_month: number;
  documents: QuestionByContract[];
}

const Profile: React.FC = () => {
  const { user, logout, updateProfile, updateProfilePhoto } = useAuth();
  const { tier, limits, isFreeTier, isPremiumTier, isPlatinumTier } = useSubscription();
  const { t } = useTranslation(['profile', 'common']);
  const { currentLanguage, switchLanguage } = useLanguage();
  const [isEditing, setIsEditing] = useState(false);
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [userState, setUserState] = useState(user?.state || '');
  const [city, setCity] = useState(user?.city || '');
  const [userLanguage, setUserLanguage] = useState(user?.language || currentLanguage);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const [photoVersion, setPhotoVersion] = useState(0);
  const [photoSrc, setPhotoSrc] = useState<string | null>(null);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [questionsByContract, setQuestionsByContract] = useState<QuestionsByContractResponse | null>(null);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [questionsError, setQuestionsError] = useState('');
  const [expandedDocuments, setExpandedDocuments] = useState<Set<number>>(new Set());
  const tierName = t(`common:subscription.tiers.${tier.toLowerCase()}.name`, { defaultValue: TIER_FEATURES[tier].name });
  const hasChanges =
    (fullName || '').trim() !== (user?.full_name || '') ||
    (userState || '') !== (user?.state || '') ||
    (city || '') !== (user?.city || '') ||
    (userLanguage || '') !== (user?.language || currentLanguage);

  // Auto-enable editing once there are changes
  useEffect(() => {
    if (hasChanges) {
      setIsEditing(true);
    }
  }, [hasChanges]);

  useEffect(() => {
    setFullName(user?.full_name || '');
    setEmail(user?.email || '');
    setUserState(user?.state || '');
    setCity(user?.city || '');
    setUserLanguage(user?.language || currentLanguage);
  }, [user, currentLanguage]);

  useEffect(() => {
    const fetchQuestionsByContract = async () => {
      if (!user) return;
      setQuestionsLoading(true);
      setQuestionsError('');
      try {
        const response = await authAPI.questionsByContract();
        setQuestionsByContract(response.data);
      } catch (err) {
        console.error('Failed to load questions by contract:', err);
        setQuestionsError(t('sections.usage.error'));
      } finally {
        setQuestionsLoading(false);
      }
    };

    fetchQuestionsByContract();
  }, [user, t]);

  useEffect(() => {
    let isMounted = true;
    let objectUrl: string | null = null;

    const loadPhoto = async () => {
      if (!user?.profile_photo_url) {
        setPhotoSrc(null);
        return;
      }
      setPhotoLoading(true);
      try {
        const res = await api.get(
          `${user.profile_photo_url}?v=${photoVersion}`,
          { responseType: 'blob' }
        );
        objectUrl = window.URL.createObjectURL(res.data);
        if (isMounted) {
          setPhotoSrc(objectUrl);
          setPhotoError('');
        }
      } catch (err) {
        if (isMounted) {
          setPhotoSrc(null);
          setPhotoError(t('sections.photo.errors.uploadFailed'));
        }
      } finally {
        if (isMounted) {
          setPhotoLoading(false);
        }
      }
    };

    loadPhoto();

    return () => {
      isMounted = false;
      if (objectUrl) {
        window.URL.revokeObjectURL(objectUrl);
      }
    };
  }, [user?.profile_photo_url, photoVersion, t]);

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setPhotoError(t('sections.photo.errors.invalidFormat'));
      return;
    }
    setPhotoError('');
    setPhotoUploading(true);
    updateProfilePhoto(file)
      .then(() => {
        setPhotoVersion(Date.now());
      })
      .catch(() => {
        setPhotoError(t('sections.photo.errors.uploadFailed'));
      })
      .finally(() => {
        setPhotoUploading(false);
        e.target.value = '';
      });
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await updateProfile({
        full_name: fullName.trim() || null,
        state: userState.trim() || null,
        city: city.trim() || null,
        language: userLanguage,
      });
      setIsEditing(false);
    } catch (err) {
      setError(t('sections.personal.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setFullName(user?.full_name || '');
    setEmail(user?.email || '');
    setUserState(user?.state || '');
    setCity(user?.city || '');
    setUserLanguage(user?.language || currentLanguage);
    setError('');
  };

  const toggleDocumentExpanded = (docId: number) => {
    const newExpanded = new Set(expandedDocuments);
    if (newExpanded.has(docId)) {
      newExpanded.delete(docId);
    } else {
      newExpanded.add(docId);
    }
    setExpandedDocuments(newExpanded);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const documentsWithQuestions = (questionsByContract?.documents || []).filter(
    (doc) => (doc?.total_questions || 0) > 0
  );

  const questionsLimitPerContractMonth = limits?.questions_per_contract_month || 0;
  const brazilStates = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
    'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
  ];

  const upgradeCtaLabel = (() => {
    const tierLower = tier.toLowerCase();
    if (tierLower === 'premium') {
      return t('sections.subscription.upgradeCtaPlatinum', { defaultValue: 'Unlock Platinum' });
    }
    if (tierLower === 'platinum') {
      return t('sections.subscription.manage', { defaultValue: 'Manage subscription' });
    }
    return t('sections.subscription.upgradeCta', { defaultValue: 'Unlock Premium' });
  })();

  return (
    <div className="container profile-page">
      <div className="profile-header">
        <h1 className="profile-title">{t('title')}</h1>
        <p className="profile-subtitle">{t('subtitle')}</p>
      </div>

      <div className="profile-content">
        {/* Profile Photo Section */}
        <section className="profile-section">
          <div className="profile-photo-container">
            <div className="profile-photo">
              {photoSrc && !photoLoading ? (
                <img
                  src={photoSrc}
                  alt="Profile"
                  className="profile-photo-img"
                />
              ) : (
                <div className="profile-photo-placeholder">
                  <UserIcon size={64} />
                </div>
              )}
              <label className="profile-photo-upload">
                <CameraIcon size={20} />
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="file-input"
                  disabled={photoUploading}
                />
              </label>
            </div>
            <div className="profile-photo-info">
              <h3 className="profile-name">{user?.full_name || t('common:nav.myAccount')}</h3>
              <p className="profile-email-display">{user?.email}</p>
              {photoError && <p className="form-helper-text">{photoError}</p>}
            </div>
          </div>
        </section>

        {/* Personal Information Section */}
        <section className="profile-section">
          <div className="profile-section-header">
            <h2 className="profile-section-title">{t('sections.personal.title')}</h2>
            <div className="profile-section-actions">
              {hasChanges && (
                <div className="profile-edit-actions">
                  <button className="btn btn-ghost soft" type="button" onClick={handleCancelEdit}>
                    {t('common:buttons.cancel')}
                  </button>
                <button
                  className="btn btn-primary strong"
                  type="button"
                  onClick={handleSave}
                  disabled={saving || !hasChanges}
                >
                  {saving ? t('sections.personal.saving') : t('sections.personal.save')}
                </button>
              </div>
            )}
            </div>
          </div>

          <div className="profile-form">
            <div className="form-group">
              <label className="form-label">{t('sections.personal.fullName')}</label>
              <input
                type="text"
                className="form-input"
                value={fullName}
                onChange={(e) => {
                  setFullName(e.target.value);
                }}
              />
            </div>

            <div className="form-grid two-col">
              <div className="form-group inline-card">
                <label className="form-label">
                  {t('sections.personal.email')}
                  <span className="info-icon" title={t('sections.personal.emailNote')}>i</span>
                </label>
                <input
                  type="email"
                  className="form-input inline-readonly"
                  value={email}
                  disabled
                />
              </div>

              <div className="form-group inline-card">
                <label className="form-label">
                  {t('sections.preferences.language')}
                  <span className="info-icon" title={t('sections.preferences.languageHelp')}>i</span>
                </label>
                <select
                  className="form-input inline-editable"
                  value={userLanguage}
                  onChange={(e) => {
                    const newLang = e.target.value as 'pt' | 'en';
                    setUserLanguage(newLang);
                    switchLanguage(newLang);
                    setIsEditing(true);
                  }}
                >
                  <option value="pt">{t('sections.preferences.languages.pt')}</option>
                  <option value="en">{t('sections.preferences.languages.en')}</option>
                </select>
              </div>

              <div className={`form-group inline-card ${isEditing ? 'is-editing' : ''}`}>
                <label className="form-label">{t('sections.personal.state')}</label>
                <select
                  className="form-input inline-editable"
                  value={userState}
                  onChange={(e) => {
                    setUserState(e.target.value);
                  }}
                >
                  <option value="">{t('sections.personal.statePlaceholder')}</option>
                  {brazilStates.map((uf) => (
                    <option key={uf} value={uf}>{uf}</option>
                  ))}
                </select>
              </div>

              <div className={`form-group inline-card ${isEditing ? 'is-editing' : ''}`}>
                <label className="form-label">{t('sections.personal.city')}</label>
                <input
                  type="text"
                  className="form-input inline-editable"
                  value={city}
                  onChange={(e) => {
                    setCity(e.target.value);
                  }}
                />
              </div>
            </div>
            {error && <p className="form-helper-text">{error}</p>}
          </div>
        </section>

        {/* Subscription Section */}
        <section className="profile-section">
          <div className="profile-section-header">
            <h2 className="profile-section-title">{t('sections.subscription.title')}</h2>
          </div>

          <div className="subscription-card">
            <div className="subscription-header">
              <div className={`subscription-tier-badge tier-${tier.toLowerCase()}`}>
                {isPremiumTier && <Zap size={20} />}
                {isPlatinumTier && <Crown size={20} />}
                <span>{tierName}</span>
              </div>
              {!isPlatinumTier && (
                <button
                  className="btn btn-primary upgrade-cta"
                  onClick={() => setShowUpgradeModal(true)}
                >
                  {upgradeCtaLabel}
                </button>
              )}
            </div>

            <div className="subscription-limits">
              <h3>{t('sections.subscription.features')}</h3>
              <ul className="limits-list">
                <li>
                  <strong>{t('sections.subscription.contracts')}:</strong>{' '}
                  {t('sections.subscription.contractsLimit', { used: user?.subscription_usage?.contracts_this_month || 0, limit: limits?.contracts_per_month || 0 })}
                </li>
                <li style={{ position: 'relative' }}>
                  <strong>{t('sections.subscription.questions')}:</strong>{' '}
                  {(limits?.questions_per_contract_month || 0) > 0 ? (
                    <>
                      {t('sections.subscription.questionsLimit', { limit: limits?.questions_per_contract_month || 0 })}
                      <span
                        style={{
                          marginLeft: '0.5rem',
                          cursor: 'help',
                          display: 'inline-flex',
                          alignItems: 'center',
                          position: 'relative'
                        }}
                      >
                        <InfoIcon size={16} />
                      </span>
                    </>
                  ) : (
                    <span>{t('sections.subscription.notIncluded')}</span>
                  )}
                </li>
                <li>
                  <strong>{t('sections.subscription.riskAnalysis')}:</strong> {limits?.has_risk_details ? t('sections.subscription.included') : t('sections.subscription.notIncluded')}
                </li>
              </ul>
            </div>

            {/* Questions Usage Section */}
            <div className="subscription-usage">
              <h3 className="subscription-usage-title">{t('sections.usage.title')}</h3>
              {isFreeTier ? (
                <p className="subscription-usage-empty">
                  {t('sections.subscription.notIncluded')}
                </p>
              ) : questionsLoading ? (
                <p className="subscription-usage-loading">{t('sections.usage.loading')}</p>
              ) : questionsError ? (
                <p className="subscription-usage-empty">{questionsError}</p>
              ) : questionsByContract && documentsWithQuestions.length > 0 ? (
                <>
                  <div className="usage-summary" style={{
                    display: 'flex',
                    gap: '1.5rem',
                    marginBottom: '1rem',
                    padding: '1rem',
                    background: '#f8f9fa',
                    borderRadius: '8px'
                  }}>
                    <div>
                      <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem' }}>
                        {t('sections.usage.totalQuestions')} ({t('sections.usage.allTime')})
                      </div>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#2c5282' }}>
                        {questionsByContract.total_questions_all_time}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem' }}>
                        {t('sections.usage.thisMonth')}
                      </div>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#2d3748' }}>
                        {questionsByContract.total_questions_this_month}
                      </div>
                    </div>
                  </div>

                  <div className="questions-by-document" style={{ marginTop: '1rem' }}>
                    <h4 style={{ marginBottom: '0.75rem', fontSize: '1rem', color: '#2d3748' }}>
                      {t('sections.usage.byContract')}
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      {documentsWithQuestions.map((doc) => (
                        <div
                          key={doc.document_id}
                          style={{
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            transition: 'box-shadow 0.2s',
                          }}
                        >
                          <div
                            onClick={() => toggleDocumentExpanded(doc.document_id)}
                            style={{
                              padding: '1rem',
                              background: expandedDocuments.has(doc.document_id) ? '#f7fafc' : 'white',
                              cursor: 'pointer',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: '600', color: '#2d3748', marginBottom: '0.25rem' }}>
                                {doc.document_name}
                              </div>
                              <div style={{ fontSize: '0.85rem', color: '#718096' }}>
                                {doc.total_questions} {t('sections.usage.totalCount')}
                                {doc.questions_this_month > 0 && (
                                  <span style={{ marginLeft: '0.5rem', color: '#4299e1' }}>
                                    • {doc.questions_this_month} {t('sections.usage.monthCount')}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.75rem'
                            }}>
                              <div style={{
                                background: '#edf2f7',
                                padding: '0.35rem 0.75rem',
                                borderRadius: '12px',
                                fontSize: '0.85rem',
                                fontWeight: '600',
                                color: '#4a5568'
                              }}>
                                {questionsLimitPerContractMonth > 0
                                  ? `${doc.questions_this_month} / ${questionsLimitPerContractMonth}`
                                  : doc.questions_this_month}
                              </div>
                              <span style={{
                                fontSize: '1.2rem',
                                color: '#a0aec0',
                                transition: 'transform 0.2s',
                                transform: expandedDocuments.has(doc.document_id) ? 'rotate(180deg)' : 'rotate(0deg)',
                                display: 'inline-block'
                              }}>
                                ▼
                              </span>
                            </div>
                          </div>

                          {expandedDocuments.has(doc.document_id) && doc.recent_questions.length > 0 && (
                            <div style={{
                              padding: '1rem',
                              background: '#f7fafc',
                              borderTop: '1px solid #e2e8f0'
                            }}>
                              <div style={{
                                fontSize: '0.9rem',
                                fontWeight: '600',
                                color: '#4a5568',
                                marginBottom: '0.75rem'
                              }}>
                                {t('sections.usage.recentQuestions')}
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                {doc.recent_questions.map((q) => (
                                  <div
                                    key={q.id}
                                    style={{
                                      background: 'white',
                                      padding: '0.75rem',
                                      borderRadius: '6px',
                                      border: '1px solid #e2e8f0'
                                    }}
                                  >
                                    <div style={{
                                      fontSize: '0.85rem',
                                      fontWeight: '600',
                                      color: '#2d3748',
                                      marginBottom: '0.25rem'
                                    }}>
                                      {q.question}
                                    </div>
                                    <div style={{
                                      fontSize: '0.8rem',
                                      color: '#718096',
                                      marginTop: '0.25rem'
                                    }}>
                                      {formatDate(q.created_at)}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <p className="subscription-usage-empty">
                  {t('sections.usage.noQuestions')}
                </p>
              )}
            </div>
          </div>
        </section>

        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
        />

        {/* Danger Zone */}
        <section className="profile-section">
          <div className="profile-section-header">
            <h2 className="profile-section-title">{t('sections.danger.title')}</h2>
          </div>

          <div className="danger-zone">
            <button
              className="btn btn-danger"
              onClick={logout}
            >
              {t('sections.danger.logout')}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Profile;
